// The studio (design language F): one card to make a cover. Who sings (one
// voice, or a duet or group with "Add voice"), the song (upload, library,
// type words, an AI song or a recording), how it sounds (a summary row that
// opens the Sound sheet), Create. It replaces the CreateModal experience
// and the old /duets builder, submits the exact body CreateModal does
// (components/modals/conversionRequest.js) and lands on
// /conversion/[shortid] like the modal.
//
// ?voice=<urlParam or _id> preselects a voice (voice pages link here), so
// from a voice page it is just: add a song, Create. ?voices=a,b,c preselects
// a duet or group; /duets redirects here with it.
import React, { useCallback, useEffect, useReducer, useState } from "react";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import toast from "react-hot-toast";
import {
  Check,
  ChevronRight,
  Clapperboard,
  Plus,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { Spinner } from "@jammable/ui-components/Loader";
import {
  MAX_SONGS,
  MAX_VOICES,
  initialSettingsState,
  settingsReducer,
} from "@/components/modals/conversionRequest";
import { StudioSheet, VoiceImage } from "@/components/studio/StudioChrome";
import VoicePicker, {
  DuetSuggestions,
  VoiceSeats,
} from "@/components/studio/VoicePicker";
import { SongArea } from "@/components/studio/SongPicker";
import SoundOptions, {
  soundSummary,
  useEffectPresets,
} from "@/components/studio/SoundOptions";
import useCreateCover from "@/components/studio/useCreateCover";
import WhoSings, {
  type Stretch,
  whoSingsSummary,
} from "@/components/studio/WhoSings";
import VideoOptions, {
  DEFAULT_MARBLE_RUN,
  MarbleRunLook,
  marbleRunTemplate,
} from "@/components/studio/VideoOptions";
import { useAuth } from "@/context/AuthProvider";
import { useModal } from "@/context/ModalContext";
import useAuthModal from "@/hooks/useAuthModal";
import useSubscription from "@/hooks/useSubscription";
import UpgradeModal from "@/components/modals/UpgradeModal";
import CreatorUpsellSheet, {
  canUpsellToCreator,
} from "@/components/upsell/CreatorUpsellSheet";
import { getSubscriptionStatus } from "@jammable/ui-payments-feature/src/utils/subscriptions";
import CreditPackModal from "@/components/modals/CreditPackModal";
import mongoose from "mongoose";
import connectToDb from "@/utils/mongodb";
import { auth } from "@/utils/firebaseAdmin";
import { MODEL_PROJECTION } from "./api/model";

/* eslint-disable @typescript-eslint/no-explicit-any */

interface CreatePageProps {
  initialVoices: any[];
}

// Which voice the picker sheet is for: replace the one at `index`, or add.
type VoiceSheet = { mode: "replace"; index: number } | { mode: "add" } | null;

// What the Create button says: the next missing thing, then the action.
const createLabel = (voices: any[], song: any | null) =>
  voices.length === 0
    ? "Pick a voice first"
    : !song
      ? "Add audio"
      : "Create cover";

const SectionHead = ({
  title,
  note,
  done,
}: {
  title: string;
  note?: string;
  done?: boolean;
}) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-2 tw-pt-1">
    <h2 className="jam-display tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-[20px] tw-font-extrabold">
      {title}
      {/* What's left at a glance: a tick once the section is filled. */}
      {done && (
        <span className="tw-flex tw-h-[22px] tw-w-[22px] tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
          <Check size={13} strokeWidth={3.5} />
        </span>
      )}
    </h2>
    {note && (
      <span className="tw-text-xs tw-font-bold tw-text-jam-muted">{note}</span>
    )}
  </div>
);

const CreatePage = ({ initialVoices }: CreatePageProps) => {
  const router = useRouter();
  const [selectedVoices, setSelectedVoices] = useState<any[]>(initialVoices);
  const [selectedSongsList, setSelectedSongsList] = useState<any[]>([]);
  const [settings, dispatchSettings] = useReducer(
    settingsReducer,
    initialSettingsState,
  );
  const { submit, loading } = useCreateCover();
  const { data: effects } = useEffectPresets();
  const [voiceSheet, setVoiceSheet] = useState<VoiceSheet>(null);
  const [soundOpen, setSoundOpen] = useState(false);
  // Marble Run: the look being edited in the sheet, and whether it's on.
  const [marbleLook, setMarbleLook] =
    useState<MarbleRunLook>(DEFAULT_MARBLE_RUN);
  const [marbleOn, setMarbleOn] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  // Basic and secret plans have no Marble Run coins: the Creator offer, then
  // straight into Marble Run once upgraded.
  const [marbleUpsell, setMarbleUpsell] = useState(false);
  const { user } = useAuth();
  const { openModal } = useModal();
  const { checkAuthAndExecute } = useAuthModal();
  const { hasCreatorPlan, hasPowerPlan } = useSubscription();

  const song = selectedSongsList[0] ?? null;
  // Who sings when (duets and groups): null leaves it to Jammable. Starts
  // over whenever the voices or the song change.
  const [whoSings, setWhoSings] = useState<Stretch[] | null>(null);
  const [whoOpen, setWhoOpen] = useState(false);
  const whoSingsKey = `${selectedVoices.map((v) => v._id).join(",")}|${song?.url ?? ""}`;
  useEffect(() => setWhoSings(null), [whoSingsKey]);
  // A file we can play and draw here; links and generated songs aren't one.
  const playableSongUrl =
    song?.url &&
    !["tts", "generate"].includes(song.sourceMode) &&
    !/youtube\.com|youtu\.be|spotify\.com|soundcloud\.com|tiktok\.com/i.test(
      song.url,
    )
      ? (song.url as string)
      : null;

  // Mirror the voices into the URL so a refresh comes back to them
  // (re-fetched server-side): ?voice= for one, ?voices= for a duet.
  const voiceQuery =
    selectedVoices.length > 1
      ? { voices: selectedVoices.map((v) => v._id).join(",") }
      : selectedVoices[0]
        ? { voice: selectedVoices[0].urlParam || selectedVoices[0]._id }
        : {};
  const voiceQueryKey = JSON.stringify(voiceQuery);
  useEffect(() => {
    if (!router.isReady) return;
    const { voice, voices, step, ...rest } = router.query;
    const current = JSON.stringify({
      ...(voices ? { voices } : {}),
      ...(voice && !voices ? { voice } : {}),
    });
    if (current === voiceQueryKey && !step) return;
    router.replace(
      { pathname: "/create", query: { ...rest, ...JSON.parse(voiceQueryKey) } },
      undefined,
      { shallow: true, scroll: false },
    );
  }, [router, voiceQueryKey]);

  // Pitch is stored per voice; drop it for any voice that leaves the cover.
  const clearPitch = (voice: any) =>
    dispatchSettings({
      type: "REMOVE_PITCH_SCALING",
      payload: { voiceId: voice._id },
    });

  const closeVoiceSheet = useCallback(() => setVoiceSheet(null), []);
  const closeSoundSheet = useCallback(() => setSoundOpen(false), []);
  const closeVideoSheet = useCallback(() => setVideoOpen(false), []);
  const marbleQuota = user?.quota?.marbleRun;
  const coinsLeft = marbleQuota
    ? marbleQuota.isUnlimited
      ? "Unlimited coins on Power"
      : `${marbleQuota.remainingCoins ?? 0} ${marbleQuota.remainingCoins === 1 ? "coin" : "coins"} left`
    : null;

  // A Marble Run costs a coin (Creator gets 50 a month, Power unlimited).
  // Without coins the row opens the paywall, or a coin pack for a plan that
  // has run out, the same branches CreateModal's Marble Run button took.
  const openVideo = () => {
    // Empty continuation: quota hasn't loaded right after sign-in, so
    // resuming would wrongly show subscribers the paywall.
    if (!checkAuthAndExecute(() => {}, "Sign up to make a video")) return;
    if (!user?.quota?.marbleRun?.hasCoins) {
      if (hasCreatorPlan() || hasPowerPlan()) {
        openModal("credit-pack-modal", <CreditPackModal />, {
          className: "md:tw-w-[480px] !tw-p-0 tw-overflow-hidden",
        });
      } else if (canUpsellToCreator(user)) {
        setMarbleUpsell(true);
      } else {
        openModal(
          "upgrade-modal",
          <UpgradeModal
            from="create-video"
            dailyLimit={false}
            message="Upgrade to Creator to make Marble Run videos"
          />,
          { className: "tw-p-0" },
        );
      }
      return;
    }
    setVideoOpen(true);
  };

  const handleAddVoice = (voice: any) => {
    if (selectedVoices.some((v) => v._id === voice._id)) return false;
    // Same limit as CreateModal's handleVoiceSelected.
    if (selectedVoices.length >= MAX_VOICES) {
      toast.error(`You can select a maximum of ${MAX_VOICES} voices.`);
      return false;
    }
    setSelectedVoices([...selectedVoices, voice]);
    return true;
  };

  const handleVoicePicked = (voice: any) => {
    if (!voiceSheet) return;
    const elsewhere = (i: number) =>
      selectedVoices.some((v, j) => j !== i && v._id === voice._id);
    if (voiceSheet.mode === "add") {
      if (!handleAddVoice(voice)) return;
    } else {
      const { index } = voiceSheet;
      if (elsewhere(index)) {
        toast.error(`${voice.artistname} is already in this cover.`);
        return;
      }
      const old = selectedVoices[index];
      if (old && old._id !== voice._id) clearPitch(old);
      const next = [...selectedVoices];
      next[index] = voice;
      setSelectedVoices(next);
    }
    setVoiceSheet(null);
  };

  const handleRemoveVoice = (voice: any) => {
    clearPitch(voice);
    // A race needs two voices.
    if (selectedVoices.length <= 2) setMarbleOn(false);
    setSelectedVoices(selectedVoices.filter((v) => v._id !== voice._id));
  };

  // One song per cover (MAX_SONGS); picking another replaces it.
  const handleSongPicked = (picked: any) =>
    setSelectedSongsList((prev) => [...prev, picked].slice(-MAX_SONGS));

  const ready = selectedVoices.length > 0 && !!song;
  const handleCreate = () =>
    submit({
      selectedVoices,
      selectedSongsList,
      settings,
      marbleRun: marbleOn ? marbleRunTemplate(marbleLook) : undefined,
      voiceTimes: !marbleOn && whoSings?.length ? whoSings : undefined,
    });
  const group = selectedVoices.length > 1;

  return (
    <div className="tw-bg-white tw-text-jam-ink">
      <NextSeo
        title="Create a cover | Jammable"
        description="Pick a voice, add audio you have the rights to use, and make an AI cover on Jammable."
        noindex
      />
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[720px] tw-flex-col tw-gap-5 tw-px-4 tw-pb-28 tw-pt-4 md:tw-pb-16 md:tw-pt-8">
        <h1 className="jam-display tw-m-0 tw-text-center tw-text-[36px] tw-font-extrabold tw-leading-tight tw-tracking-[-0.04em] md:tw-text-[48px]">
          {selectedVoices.length === 1
            ? `Make ${selectedVoices[0].artistname} sing`
            : selectedVoices.length > 1
              ? "Make them sing"
              : "Make a cover"}
        </h1>

        <div className="tw-flex tw-flex-col tw-gap-2.5 tw-rounded-[36px] tw-bg-jam-lav tw-p-3 md:tw-p-4">
          {/* Who sings */}
          <SectionHead
            done={selectedVoices.length > 0}
            title={group ? "Who sings" : "Voice"}
            note={
              group ? `${selectedVoices.length} of ${MAX_VOICES}` : undefined
            }
          />
          {selectedVoices.length === 0 ? (
            <button
              type="button"
              onClick={() => setVoiceSheet({ mode: "replace", index: 0 })}
              className="tw-flex tw-cursor-pointer tw-items-center tw-gap-4 tw-rounded-[24px] tw-border-0 tw-bg-white tw-p-3 tw-pr-4 tw-text-left tw-text-jam-ink tw-transition-colors hover:tw-bg-jam-line"
            >
              <span className="tw-flex tw-h-[68px] tw-w-[68px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-purple tw-text-white">
                <Plus size={28} strokeWidth={2.75} />
              </span>
              <span className="tw-flex-grow">
                <span className="jam-display tw-block tw-text-[19px] tw-font-extrabold">
                  Pick a voice
                </span>
                <span className="tw-block tw-text-[13px] tw-font-bold tw-text-jam-muted">
                  Any of 100,000+ voices, or your own
                </span>
              </span>
              <ChevronRight
                size={20}
                strokeWidth={2.5}
                className="tw-shrink-0 tw-text-jam-muted"
              />
            </button>
          ) : (
            <>
              <VoiceSeats
                voices={selectedVoices}
                max={MAX_VOICES}
                onChange={(index) => setVoiceSheet({ mode: "replace", index })}
                onRemove={handleRemoveVoice}
                onAdd={() => setVoiceSheet({ mode: "add" })}
              />
              {selectedVoices.length < MAX_VOICES && (
                <DuetSuggestions
                  voices={selectedVoices}
                  onAdd={handleAddVoice}
                />
              )}
            </>
          )}

          {/* The song */}
          <SectionHead title="Audio" done={!!song} />
          <SongArea
            selectedVoices={selectedVoices}
            song={song}
            onPick={handleSongPicked}
            onClear={() => setSelectedSongsList([])}
          />
          {/* Who sings when: Auto pick, or the user does. */}
          {group && song && !marbleOn && (
            <button
              type="button"
              onClick={() => setWhoOpen(true)}
              className="tw-mt-1 tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[24px] tw-border-0 tw-bg-white tw-p-3 tw-pr-4 tw-text-left tw-transition-colors tw-text-jam-ink hover:tw-bg-jam-line"
            >
              <span
                className={`tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full ${whoSings?.length ? "tw-bg-jam-lime tw-text-jam-ink" : "tw-bg-jam-lav tw-text-jam-purple"}`}
              >
                <Users size={18} strokeWidth={2.5} />
              </span>
              <span className="tw-min-w-0 tw-flex-grow">
                <span className="tw-block tw-text-[15px] tw-font-extrabold">
                  Who sings when
                </span>
                <span className="tw-block tw-truncate tw-text-xs tw-font-bold tw-text-jam-muted">
                  {whoSingsSummary(whoSings)}
                </span>
              </span>
              <ChevronRight
                size={20}
                strokeWidth={2.5}
                className="tw-shrink-0 tw-text-jam-muted"
              />
            </button>
          )}

          {/* How it sounds */}
          {selectedVoices.length > 0 && (
            <button
              type="button"
              onClick={() => setSoundOpen(true)}
              className="tw-mt-1 tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[24px] tw-border-0 tw-bg-white tw-p-3 tw-pr-4 tw-text-left tw-transition-colors tw-text-jam-ink hover:tw-bg-jam-line"
            >
              <span className="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-purple">
                <SlidersHorizontal size={18} strokeWidth={2.5} />
              </span>
              <span className="tw-min-w-0 tw-flex-grow">
                <span className="tw-block tw-text-[15px] tw-font-extrabold">
                  Sound
                </span>
                <span className="tw-block tw-truncate tw-text-xs tw-font-bold tw-text-jam-muted">
                  {soundSummary(settings, selectedVoices, effects)}
                </span>
              </span>
              <ChevronRight
                size={20}
                strokeWidth={2.5}
                className="tw-shrink-0 tw-text-jam-muted"
              />
            </button>
          )}

          {/* A video. Only Marble Run is picked here (it decides who sings
              when); the rest is made in /studio after the cover. A race
              needs at least two voices. */}
          {selectedVoices.length > 1 && (
            <button
              type="button"
              onClick={openVideo}
              className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[24px] tw-border-0 tw-bg-white tw-p-3 tw-pr-4 tw-text-left tw-transition-colors tw-text-jam-ink hover:tw-bg-jam-line"
            >
              <span
                className={`tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full ${marbleOn ? "tw-bg-jam-lime tw-text-jam-ink" : "tw-bg-jam-lav tw-text-jam-purple"}`}
              >
                <Clapperboard size={18} strokeWidth={2.5} />
              </span>
              <span className="tw-min-w-0 tw-flex-grow">
                <span className="tw-block tw-text-[15px] tw-font-extrabold">
                  Video
                </span>
                <span className="tw-block tw-truncate tw-text-xs tw-font-bold tw-text-jam-muted">
                  {marbleOn
                    ? `Marble Run · 1 coin${coinsLeft ? ` · ${coinsLeft}` : ""}`
                    : "Race your voices in a Marble Run"}
                </span>
              </span>
              <ChevronRight
                size={20}
                strokeWidth={2.5}
                className="tw-shrink-0 tw-text-jam-muted"
              />
            </button>
          )}

          <button
            type="button"
            onClick={handleCreate}
            disabled={!ready || loading}
            // Sticks to the bottom of the screen while the card scrolls past
            // (above the phone tab bar), then settles at the card's end. The
            // lavender ring is the card colour, so it reads as floating.
            className="tw-sticky tw-bottom-[96px] tw-z-20 tw-mt-1 tw-flex tw-h-[60px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lime tw-text-[17px] tw-font-extrabold tw-text-jam-ink tw-ring-[6px] tw-ring-jam-lav tw-transition-colors hover:tw-bg-[#C4EE00] disabled:tw-cursor-not-allowed disabled:tw-bg-white disabled:tw-text-jam-muted md:tw-bottom-6"
          >
            {loading ? (
              <Spinner className="tw-border-3 tw-border-jam-ink" size={4} />
            ) : (
              createLabel(selectedVoices, song)
            )}
          </button>
        </div>
      </div>

      <StudioSheet
        open={!!voiceSheet}
        title={voiceSheet?.mode === "add" ? "Add a voice" : "Pick a voice"}
        onClose={closeVoiceSheet}
      >
        <VoicePicker
          // Remount per open so the default filter follows the mode.
          key={voiceSheet?.mode ?? "closed"}
          exclude={voiceSheet?.mode === "add" ? selectedVoices : []}
          pairWith={voiceSheet?.mode === "add" ? selectedVoices : []}
          onPick={handleVoicePicked}
        />
      </StudioSheet>

      <StudioSheet
        open={whoOpen}
        title="Who sings when"
        onClose={() => setWhoOpen(false)}
        className="md:tw-max-w-[820px]"
        footer={
          <button
            type="button"
            onClick={() => setWhoOpen(false)}
            className="tw-h-12 tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-ink tw-text-[15px] tw-font-extrabold tw-text-white"
          >
            Done
          </button>
        }
      >
        <WhoSings
          voices={selectedVoices}
          songUrl={playableSongUrl}
          value={whoSings}
          onChange={setWhoSings}
        />
      </StudioSheet>

      <StudioSheet
        open={soundOpen}
        title="Sound"
        onClose={closeSoundSheet}
        footer={
          <button
            type="button"
            onClick={closeSoundSheet}
            className="tw-h-12 tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-ink tw-text-[15px] tw-font-extrabold tw-text-white"
          >
            Done
          </button>
        }
      >
        <SoundOptions
          voices={selectedVoices}
          song={song}
          settings={settings}
          dispatchSettings={dispatchSettings}
        />
      </StudioSheet>

      <CreatorUpsellSheet
        open={marbleUpsell}
        reason="marble-run"
        onClose={() => setMarbleUpsell(false)}
        // Creator's 50 coins come with the upgrade invoice, a moment after.
        ready={(u) =>
          getSubscriptionStatus(u?.subscriptions) === "creator" &&
          !!u?.quota?.marbleRun?.hasCoins
        }
        onUpgraded={() => {
          setMarbleUpsell(false);
          toast.success("You're on Creator. Pick your Marble Run.");
          setVideoOpen(true);
        }}
      />

      <StudioSheet
        open={videoOpen}
        title="Marble Run"
        onClose={closeVideoSheet}
        footer={
          <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2">
            {coinsLeft && (
              <div className="tw-text-center tw-text-xs tw-font-bold tw-text-jam-muted">
                {coinsLeft}
              </div>
            )}
            <div className="tw-flex tw-gap-2">
              {marbleOn && (
                <button
                  type="button"
                  onClick={() => {
                    setMarbleOn(false);
                    closeVideoSheet();
                  }}
                  className="tw-h-12 tw-flex-1 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-lav tw-text-[15px] tw-font-extrabold tw-text-jam-ink"
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setMarbleOn(true);
                  closeVideoSheet();
                }}
                className="tw-h-12 tw-flex-[2] tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-ink tw-text-[15px] tw-font-extrabold tw-text-white"
              >
                {marbleOn ? "Done" : "Add Marble Run · 1 coin"}
              </button>
            </div>
          </div>
        }
      >
        <VideoOptions
          look={marbleLook}
          onChange={setMarbleLook}
          voices={selectedVoices}
        />
      </StudioSheet>
    </div>
  );
};

export default CreatePage;

// Preselect voices the way pages/[model].js loads modelData: read-only
// lookup by urlParam (or _id), hidden if removed, deleted, still training or
// private to someone else. Order follows the URL; the first is the lead.
export const getServerSideProps: GetServerSideProps<CreatePageProps> = async (
  context,
) => {
  const list = (raw: unknown) =>
    (Array.isArray(raw) ? raw.join(",") : typeof raw === "string" ? raw : "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  const wanted = [
    ...new Set([...list(context.query.voice), ...list(context.query.voices)]),
  ].slice(0, MAX_VOICES);
  if (wanted.length === 0) {
    // Plain /create has no per-request data (identical props for everyone),
    // but as getServerSideProps it was a function invocation on every hit:
    // always x-vercel-cache MISS, 230-330ms TTFB from London on 2026-10-09.
    context.res.setHeader(
      "Cache-Control",
      "public, s-maxage=3600, stale-while-revalidate=86400",
    );
    return { props: { initialVoices: [] } };
  }

  try {
    const Model = (await import("../models/Model")).default;

    let firebaseUid: string | undefined;
    const token = context.req?.cookies?.token;
    if (token) {
      try {
        firebaseUid = (await auth.verifyIdToken(token)).uid;
      } catch {
        // Invalid token: continue logged out.
      }
    }

    await connectToDb();
    const ids = wanted
      .filter((v) => /^[a-f0-9]{24}$/i.test(v))
      .map((v) => new mongoose.Types.ObjectId(v));
    const models = await Model.aggregate([
      {
        $match: {
          $or: [{ urlParam: { $in: wanted } }, { _id: { $in: ids } }],
        },
      },
      { $limit: wanted.length * 2 },
      // accessIds only for the private-voice check below; never sent.
      { $project: { ...MODEL_PROJECTION, accessIds: 1 } },
    ]).exec();

    const usable = (model: any) =>
      !model.isRemoved &&
      !model.deleted &&
      model.training !== true &&
      (!model.private || model.accessIds?.includes(firebaseUid));
    const initialVoices = wanted
      .map((w) =>
        models.find((m: any) => m.urlParam === w || String(m._id) === w),
      )
      .filter((m: any, i, arr) => m && usable(m) && arr.indexOf(m) === i)
      .map((model: any) =>
        JSON.parse(
          JSON.stringify({
            ...model,
            accessIds: undefined,
            comments: undefined,
            librarySongDemos: undefined,
            demos: undefined,
          }),
        ),
      );

    if (token) {
      context.res.setHeader("Cache-Control", "private, no-store");
    }
    return { props: { initialVoices } };
  } catch (error) {
    console.error("Studio voice preselect failed:", error);
    return { props: { initialVoices: [] } };
  }
};
